import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { searchByUsername } from '../../services/api/userService';
import './PlayerSearch.css';

/** "12 matches · 7 wins · Volleyball", or "No matches yet". */
export const playerStatsLine = (user) => {
  const played = Number(user?.matches_played) || 0;
  if (played === 0) return 'No matches yet';
  const won = Number(user?.matches_won) || 0;
  const parts = [
    `${played} ${played === 1 ? 'match' : 'matches'}`,
    `${won} ${won === 1 ? 'win' : 'wins'}`,
  ];
  if (user.top_sport) parts.push(user.top_sport);
  return parts.join(' · ');
};

const initials = (user) =>
  `${(user.first_name || user.username || '?')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase();

/**
 * Standalone player-search bar.
 * On selecting a result it navigates to /players/:id.
 * The dropdown is portalled to <body> with fixed positioning so it is never
 * clipped by a scrolling card, and scrolling inside it does not scroll the page.
 */
function PlayerSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const wrapRef = useRef(null);
  const listRef = useRef(null);

  const updatePos = useCallback(() => {
    if (!wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  // Keep the dropdown under the input while the page scrolls or resizes
  useEffect(() => {
    if (!open) return undefined;
    updatePos();
    window.addEventListener('scroll', updatePos, true);
    window.addEventListener('resize', updatePos);
    return () => {
      window.removeEventListener('scroll', updatePos, true);
      window.removeEventListener('resize', updatePos);
    };
  }, [open, results, updatePos]);

  // Close on a click outside the input or the dropdown
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || listRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [open]);

  // Debounced search — fires 300 ms after user stops typing
  useEffect(() => {
    const term = query.trim();
    if (term.length < 2) {
      setResults([]);
      setSearched(false);
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        setSearching(true);
        const res = await searchByUsername(term);
        setResults(res.data?.users || []);
      } catch (err) {
        console.error('Player search error:', err);
        setResults([]);
      } finally {
        setSearching(false);
        setSearched(true);
        setActive(-1);
        setOpen(true);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [query]);

  const handleSelect = (user) => {
    setQuery('');
    setResults([]);
    setOpen(false);
    navigate(`/players/${user.short_code || user.uuid || user.id}`);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      setOpen(false);
      return;
    }
    if (!open || results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => (i + 1) % results.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => (i <= 0 ? results.length - 1 : i - 1));
    } else if (e.key === 'Enter' && active >= 0) {
      e.preventDefault();
      handleSelect(results[active]);
    }
  };

  const showList = open && results.length > 0;
  const showEmpty = open && searched && !searching && results.length === 0 && query.trim().length >= 2;

  return (
    <div className="player-search" ref={wrapRef}>
      <div className="player-search__wrapper">
        <span className="player-search__icon" aria-hidden="true">🔍</span>
        <input
          className="player-search__input"
          type="search"
          placeholder="Search players by name or username…"
          aria-label="Search players"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          onKeyDown={onKeyDown}
          autoComplete="off"
          maxLength={50}
        />
        {searching && <span className="player-search__spinner" />}
      </div>

      {(showList || showEmpty) && ReactDOM.createPortal(
        <ul
          ref={listRef}
          className="player-search__dropdown"
          role="listbox"
          style={{
            top: `${dropdownPos.top}px`,
            left: `${dropdownPos.left}px`,
            width: `${dropdownPos.width}px`,
          }}
        >
          {showEmpty && <li className="player-search__no-results">No players found</li>}
          {results.map((user, index) => (
            <li
              key={user.id}
              role="option"
              aria-selected={index === active}
              className={`player-search__result${index === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(index)}
              onClick={() => handleSelect(user)}
            >
              {user.avatar_url ? (
                <img className="player-search__avatar" src={user.avatar_url} alt="" />
              ) : (
                <span className="player-search__avatar player-search__avatar--initials">{initials(user)}</span>
              )}
              <span className="player-search__result-main">
                <span className="player-search__result-name">
                  {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
                </span>
                <span className="player-search__result-sub">
                  <span className="player-search__result-username">@{user.username}</span>
                  <span className="player-search__result-stats">{playerStatsLine(user)}</span>
                </span>
              </span>
            </li>
          ))}
        </ul>,
        document.body
      )}
    </div>
  );
}

export default PlayerSearch;
